import { getSkill } from '../shared/skills';
import { FACTIONS } from '../shared/factions';
import { playFactionStinger } from '../game/audio/bgm-player';
import type { FactionId } from '../shared/types';

const DEFAULT_DURATION_MS = 2500;
const STINGER_DURATION_MS = 1500;
const NEUTRAL_COLOR_HEX = '#ffd54f';
const NEUTRAL_EMOJI = '✨';

/**
 * Pokémon-evolution-style full-screen ceremony for a skill level-up.
 *   - 0.5s fade-in dark overlay
 *   - 1.0s emblem scale 0 → 1.2 + faction-coloured aura pulse
 *   - 1.0s headline rise + glow
 *   - IDEA-014 faction stinger SFX
 *   - 0.5s fade-out
 *   - 2.5s total duration (auto-dismiss); also closes on click.
 * Lv5 graduation flips the headline to a MAX/畢業 variant.
 */
export function showLevelUpCelebration(
  container: HTMLElement,
  skillId: string,
  fromLv: number,
  toLv: number,
  onDismiss: () => void,
): void {
  const skill = getSkill(skillId);
  const isMax = toLv >= 5;

  let factionEmoji = NEUTRAL_EMOJI;
  let factionColorHex = NEUTRAL_COLOR_HEX;
  let factionId: FactionId | null = null;
  if (skill && skill.faction !== 'builtin') {
    factionId = skill.faction;
    const faction = FACTIONS[factionId];
    factionEmoji = faction.emoji;
    factionColorHex = '#' + faction.colorPrimary.toString(16).padStart(6, '0');
  }
  const skillName = skill?.displayName ?? '神秘技能';

  const headline = isMax
    ? '✨ 大成 MAX 畢業 ✨'
    : `Lv ${fromLv} → Lv ${toLv}!`;
  const subline = `${factionEmoji} ${skillName}`;

  const overlay = document.createElement('div');
  overlay.className = 'level-up-celebration';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-live', 'assertive');
  overlay.innerHTML = `
    <div class="luc-card">
      <div class="luc-emoji" style="color:${factionColorHex};">${factionEmoji}</div>
      <div class="luc-headline" style="color:${factionColorHex};">${headline}</div>
      <div class="luc-subline">${subline}</div>
      ${isMax ? '<div class="luc-crown">👑</div>' : ''}
      <div class="luc-hint">點擊任意處繼續</div>
    </div>
  `;
  container.appendChild(overlay);

  if (factionId) {
    playFactionStinger(factionId, STINGER_DURATION_MS);
  }

  let dismissed = false;
  const finish = (): void => {
    if (dismissed) return;
    dismissed = true;
    overlay.remove();
    onDismiss();
  };
  overlay.addEventListener('click', finish);
  setTimeout(finish, DEFAULT_DURATION_MS);
}
